import * as React from 'react'
import { lightTheme } from '@/customTheme'

/** Which corner of the frame the dot sits in. */
export type BadgePositionAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

/*
 * Geometry from Figma "Position batch" (node 12910:45061), in a 24x24 box.
 *
 * The frame's 20x14 at (2,5) are stroke-CENTERLINE bounds, not a CSS border-box
 * — Figma and SVG both straddle the stroke, so the path takes them verbatim. The
 * resulting 1px inward overhang (inner edges x 3..21, y 6..18) is what leaves a
 * 1px gap around the dot at (4,7); reading them as a border-box would sit the dot
 * flush and halve the corner radius.
 */
const BOX = 24
const FRAME = { x: 2, y: 5, width: 20, height: 14, rx: 3, strokeWidth: 2 }
const DOT = { size: 6, rx: 2 }
/** 1px clear of the frame's inner edges (x 3..21, y 6..18). */
const DOT_X_LEFT = 4
const DOT_X_RIGHT = 14
const DOT_Y_TOP = 7
const DOT_Y_BOTTOM = 11

export interface BadgePositionIconProps {
  size?: number
  /** Frame stroke colour. */
  color?: string
  /** Dot colour. Defaults to the accent pink on a full-contrast frame and to the
   *  frame's own colour on a muted one, so the glyph reads as a single unit. */
  dotColor?: string
  anchor?: BadgePositionAnchor
}

/**
 * Corner-position glyph: a rounded frame with a dot in one corner. One component
 * covers all four corners — pass `anchor` rather than adding a variant.
 */
function BadgePositionIcon({
  size = BOX,
  color = lightTheme.colors.blackGray,
  dotColor,
  anchor = 'top-left',
}: BadgePositionIconProps) {
  const resolvedDotColor =
    dotColor ?? (color === lightTheme.colors.blackGray ? lightTheme.colors.primary : color)
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${BOX} ${BOX}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x={FRAME.x}
        y={FRAME.y}
        width={FRAME.width}
        height={FRAME.height}
        rx={FRAME.rx}
        stroke={color}
        strokeWidth={FRAME.strokeWidth}
      />
      <rect
        x={anchor.endsWith('left') ? DOT_X_LEFT : DOT_X_RIGHT}
        y={anchor.startsWith('top') ? DOT_Y_TOP : DOT_Y_BOTTOM}
        width={DOT.size}
        height={DOT.size}
        rx={DOT.rx}
        fill={resolvedDotColor}
      />
    </svg>
  )
}

export default BadgePositionIcon
